export interface AdminUser {
  id: string;
  email: string;
  display_name: string;
  role: 'SUPERADMIN' | 'ADMIN' | 'READ_ONLY';
  authenticated: boolean;
  mfa_enabled: boolean;
  must_change_password: boolean;
}

let csrfToken = sessionStorage.getItem('cdrm_csrf') ?? '';
export function setCsrf(token: string): void {
  csrfToken = token;
  sessionStorage.setItem('cdrm_csrf', token);
}

export async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers);
  headers.set('Content-Type', 'application/json');
  if (csrfToken) headers.set('X-CSRF-Token', csrfToken);
  const response = await fetch(path, { ...options, credentials: 'include', headers });
  if (response.status === 204) return undefined as T;
  const body = (await response.json()) as {
    data?: T;
    error?: { code: string; message: string; request_id: string };
  };
  if (!response.ok || body.error)
    throw new Error(
      body.error
        ? `${body.error.message} · ${body.error.request_id}`
        : `Request failed (${response.status})`,
    );
  return body.data as T;
}

export function post<T>(path: string, body?: unknown): Promise<T> {
  return api(path, { method: 'POST', body: JSON.stringify(body ?? {}) });
}
export function patch<T>(path: string, body: unknown): Promise<T> {
  return api(path, { method: 'PATCH', body: JSON.stringify(body) });
}
export function put<T>(path: string, body: unknown): Promise<T> {
  return api(path, { method: 'PUT', body: JSON.stringify(body) });
}
export function remove<T>(path: string, body: unknown): Promise<T> {
  return api(path, { method: 'DELETE', body: JSON.stringify(body) });
}

export async function downloadRecoveryBundle(id: string): Promise<void> {
  const filename = `license-manager-${id}.lmrec`;
  const browser = window as Window & {
    showSaveFilePicker?: (options: { suggestedName: string }) => Promise<{
      createWritable: () => Promise<WritableStream<Uint8Array>>;
    }>;
  };
  const target = browser.showSaveFilePicker
    ? await browser.showSaveFilePicker({ suggestedName: filename })
    : null;
  const headers = new Headers();
  if (csrfToken) headers.set('X-CSRF-Token', csrfToken);
  const response = await fetch(`/api/admin/v1/recovery/backups/${encodeURIComponent(id)}/download`, {
    method: 'POST', credentials: 'include', headers,
  });
  if (!response.ok) throw new Error(`Recovery download failed (${response.status}).`);
  if (target && response.body) {
    await response.body.pipeTo(await target.createWritable());
    return;
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.append(link);
    link.click();
    link.remove();
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }
}
